import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import api from 'api';
import { PortfoilioLead } from 'api/portfolio/buyer';
import ResolvePagination from 'components/ResolvePagination';
import useQueryParams from 'hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import WatchlistPortfolioCard from '../../../Watchlist/components/WatchlistPortfolioCard';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './components/ProspectiveBuyersTableCells/data';
import { CUSTOM_COL_SIZES, PRIVATE_INVITATION_PAGE } from '../TableCells/data';
import { BODY_CONTENT_GENERATE } from './components/ProspectiveBuyersTableCells';
import utils from 'utils';
import { useNavigate } from 'react-router-dom';
import PortfolioPlaceholderCards from '../../../../components/PortfolioPlaceholderCards';
import { SIZE } from 'models/Size';
import usePageParams from 'hooks/usePageParams';

import './ProspectiveBuyer.styles.scss';

type ProspectiveBuyerProps = {
  className?: string;
  dataLimit: string;
  isTableView?: boolean;
  hideColumns?: string[];
  isEmpty: boolean;
  setIsEmptyPrivateInvitations: (value: boolean) => void;
};

const ProspectiveBuyer: React.FC<ProspectiveBuyerProps> = (props) => {
  const {
    className,
    dataLimit,
    isTableView,
    hideColumns,
    isEmpty,
    setIsEmptyPrivateInvitations,
  } = props;

  const classes = classNames('prospective-buyer', className);

  const [totalPages, setTotalPages] = useState(0);
  const [deals, setDeals] = useState<PortfoilioLead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [orderedColumns, setOrderedColumns] = useState(TABLE_COLUMNS);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    params: { privateInvitationSort },
    setQueryParam,
  } = useQueryParams();

  const privateInvitationPage = usePageParams(PRIVATE_INVITATION_PAGE);

  const tableColumns = useMemo(
    () => orderedColumns.filter((el) => !hideColumns.includes(el)),
    [hideColumns, orderedColumns],
  );

  const renderDeals = useMemo(
    () =>
      isLoading ? (
        <PortfolioPlaceholderCards
          numberOfCards={+dataLimit}
          size={SIZE.SMALL}
        />
      ) : (
        deals.map(
          ({
            dealRoom: {
              _id,
              stage,
              listingStage,
              createdAt,
              industry,
              dealOutcome,
              otherDealOutcome,
              portfolioName,
              isColleague,
              userId,
              user,
            },
            listing: {
              gallery,
              type,
              expirationDate,
              seekingExpressionOfInterestDate,
              slugName,
            },
          }) => (
            <WatchlistPortfolioCard
              key={_id}
              imageSrc={gallery[0]?.url}
              title={portfolioName}
              dealStage={stage}
              listingStage={listingStage}
              date={createdAt}
              isColleague={isColleague}
              dealOutcome={dealOutcome}
              otherDealOutcome={otherDealOutcome}
              listingType={type}
              industry={industry}
              seekingExpressionOfInterestDate={seekingExpressionOfInterestDate}
              expirationDate={expirationDate}
              ownerId={userId}
              owner={user}
              slugName={slugName}
            />
          ),
        )
      ),
    [dataLimit, deals, isLoading],
  );

  const tableContent = useMemo(
    () =>
      orderedColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: ({ dealRoom, listing, createdAt }: PortfoilioLead) =>
            BODY_CONTENT_GENERATE[col]({
              dealRoom,
              listing,
              createdAt,
            }),
        }),
        {},
      ),
    [orderedColumns],
  );

  const onReorderL = useCallback((c) => setOrderedColumns(c), []);

  useEffect(() => {
    if (!privateInvitationSort) return;

    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { totalPages, items },
        } = await api.portfolio.buyer.getProspectiveBuyersListings({
          $page: privateInvitationPage,
          $limit: dataLimit,
          $sort: privateInvitationSort,
          $relations: '["listing","buyer","listingEnquiry","dealRoom"]',
        });

        setTotalPages(totalPages);
        setDeals(items);
        setIsEmptyPrivateInvitations(!items.length);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    privateInvitationPage,
    privateInvitationSort,
    dataLimit,
    setIsLoading,
    setIsEmptyPrivateInvitations,
  ]);

  if (isEmpty) return null;

  return (
    <div className={classes}>
      <p className="title">{t('listingInvitations')}</p>

      <div className="resolve-page-items">
        {!isTableView ? (
          renderDeals
        ) : (
          <ReorderResizeTable
            className="portfolio-results-table"
            headingTranslations={HEADING_TRANSLATIONS}
            tableContentTransformations={tableContent}
            columns={tableColumns as Array<keyof PortfoilioLead>}
            sortingValues={SORTING_VALUES}
            isDataLoading={isLoading}
            data={deals}
            customSizes={CUSTOM_COL_SIZES}
            sort={privateInvitationSort}
            updateSort={(value) =>
              setQueryParam('privateInvitationSort', value)
            }
            onRowClick={({ listing: { industry, slugName, type } }) =>
              navigate(utils.composeBusinessUrl(industry, slugName, type))
            }
            onReorder={onReorderL}
          />
        )}
      </div>
      <ResolvePagination
        totalPages={totalPages}
        pageParam={PRIVATE_INVITATION_PAGE}
      />
    </div>
  );
};

export default ProspectiveBuyer;
